class OrderStatusSection extends HTMLElement {
  constructor() {
    super();
    this.container = this;
    this.apiUrl = 'https://byyz0gps85.execute-api.us-east-1.amazonaws.com/Production/account/api';
    this.orderCurrency = null;

    const rawPayload = sessionStorage.getItem('orderStatusAuthData');

    if (!rawPayload) {
      this._setErrorMessage(new Error('Missing sessionStorage key: orderStatusAuthData'), 'constructor');
      return;
    }

    this._fetchOrderDetails(rawPayload);
  }

  // FETCH ORDER DETAILS; THE API VALIDATES THE STORED ZIP AGAINST THE ORDER BEFORE RETURNING DETAILS
  async _fetchOrderDetails(rawPayload) {
    try {
      const payload = JSON.parse(rawPayload);
      const response = await fetch(`${this.apiUrl}/order-details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const orderData = await response.json();

      if (!response.ok || !orderData.order) {
        throw new Error(orderData.errorStatus);
      }

      // DISPLAY THE ORDER'S CURRENCY, NOT THE VIEWER'S CURRENT MARKET
      this.orderCurrency = orderData.order.totalPriceSet?.presentmentMoney?.currencyCode;

      this._renderOrder(orderData.order);
    } catch (error) {
      this._setErrorMessage(error, 'fetchOrderDetails');
    }
  }

  // ----------------------------------------------
  // RENDER
  // ----------------------------------------------

  _renderOrder(order) {
    // RENDER SECTIONS
    this._renderHeader(order);
    this._renderAlerts(order);
    this._renderLineItems(order);
    this._renderFooter(order);
    this._renderAddresses(order);
  }

  _renderHeader(order) {
    // DOM ELEMENTS
    const orderTitleElement = this.container.querySelector('[data-order-title]');
    const orderPlacedAtElement = this.container.querySelector('[data-order-placed-at]');

    // ORDER VALUES
    const orderTitle = `Order ${order.name}`;
    const orderPlacedAt = `Order placed on ${this._formatDate(order.createdAt, true)}`;

    // ASSIGN
    orderTitleElement.textContent = orderTitle;
    orderPlacedAtElement.textContent = orderPlacedAt;
  }

  // RENDERS CANCELLATION AND TRACKING BANNER
  _renderAlerts(order) {
    // DOM ELEMENTS
    const alertsContainer = this.container.querySelector('[data-order-alerts]');

    // RESET
    alertsContainer.innerHTML = '';

    // CANCELLATION ALERT
    if (order.cancelledAt) {
      const cancelAlert = this._createElements('p', ['Alert', 'Alert--large', 'Alert--error'], [{ role: 'alert' }], `Order cancelled on ${this._formatDate(order.cancelledAt, true)}`);
      alertsContainer.appendChild(cancelAlert);
      return; // NO TRACKING ALERT IF THE ORDER WAS CANCELLED
    }

    // TRACKING ALERT
    const tracking = order.fulfillments
      .flatMap((f) => f.trackingInfo)
      .find((t) => t.number);

    if (tracking) {
      alertsContainer.appendChild(this._buildTrackingAlert(tracking));
    }
  }

  // RENDERS EACH LINE ITEM ROW, PLUS A "SENT" BANNER FOR ANY ITEM WITHOUT TRACKING (E.G. GIFT CARDS)
  _renderLineItems(order) {
    // DOM ELEMENTS
    const tbody = this.container.querySelector('[data-order-line-items]');

    // RESET
    tbody.innerHTML = '';

    if (!order.lineItems?.edges?.length) return;

    // LINE ITEMS INSIDE A FULFILLMENT WITH NO TRACKING
    const untrackedLineIds = new Set();

    order.fulfillments.forEach((fulfillment) => {
      if (fulfillment.trackingInfo.some((t) => t.number)) return;
      (fulfillment.fulfillmentLineItems?.edges ?? []).forEach(({ node }) => {
        untrackedLineIds.add(node.lineItem.id);
      });
    });

    order.lineItems.edges.forEach(({ node: item }) => {
      tbody.appendChild(this._buildLineItemRow(item));

      // SKIP THE "SENT" BANNER FOR NON GIFT CARD ITEMS
      if (untrackedLineIds.has(item.id) && (item.title.toLowerCase() === 'premier yarns gift card')) {
        tbody.appendChild(this._buildSentBanner(item));
      }
    });
  }

  // RENDERS THE SUMMARY ROWS: SUBTOTAL, DISCOUNTS, SHIPPING, TAX, TOTAL
  _renderFooter(order) {
    // DOM ELEMENTS
    const tfoot = this.container.querySelector('[data-order-summary]');

    // RESET
    tfoot.innerHTML = '';

    // SUM CART-LEVEL DISCOUNTS PER APPLICATION — ONE FOOTER ROW EACH
    const cartLevelDiscounts = new Map();

    // SUBTOTAL PRICE FROM SHOPIFY INCLUDES CART LEVEL DISCOUNTS, SO WE NEED TO ADD THAT BACK TO THE SUBTOTAL
    let allDiscountTotal = 0;

    order.lineItems.edges.forEach(({ node: item }) => {
      item.discountAllocations.forEach((allocation) => {
        const app = allocation.discountApplication;
        if (app.targetSelection !== 'ALL') return;

        const amount = Number(allocation.allocatedAmountSet.presentmentMoney.amount);
        allDiscountTotal += amount;
        const entry  = cartLevelDiscounts.get(app.index) ?? { app, total: 0 };
        entry.total += amount;
        cartLevelDiscounts.set(app.index, entry);
      });
    });

    // SUMMARY ROWS
    const subtotalRow = this._buildFooterRow('Subtotal', null, Number(order.subtotalPriceSet.presentmentMoney.amount) + allDiscountTotal);
    tfoot.appendChild(subtotalRow);

    cartLevelDiscounts.forEach(({ app, total }) => {
      if (total <= 0) return;
      const discountRow = this._buildFooterRow('Discount', app.title || app.code, total, true);
      tfoot.appendChild(discountRow);
    });

    order.shippingLines.edges.forEach(({ node: shipping }) => {
      const shippingRow = this._buildFooterRow('Shipping', shipping.title, Number(shipping.discountedPriceSet.presentmentMoney.amount));
      tfoot.appendChild(shippingRow);
    });

    if (order.taxLines.length > 0) {
      order.taxLines.forEach((tax) => {
        const detail = tax.ratePercentage != null ? `${tax.title} ${tax.ratePercentage}%` : tax.title;
        const taxRow = this._buildFooterRow('Tax', detail, Number(tax.priceSet.presentmentMoney.amount));
        tfoot.appendChild(taxRow);
      });
    } else {
      // SHOPIFY DOESN'T RETURN INDIVIDUAL TAX LINES FOR ZERO-TAX ORDERS, USE THE DEFAULT TAX LINE
      const taxRow = this._buildFooterRow('Tax', null, Number(order.totalTaxSet.presentmentMoney.amount));
      tfoot.appendChild(taxRow);
    }

    const totalRow = this._buildTotalRow(Number(order.totalPriceSet.presentmentMoney.amount));
    tfoot.appendChild(totalRow);
  }

  // RENDERS THE SHIPPING AND BILLING ADDRESS
  _renderAddresses(order) {
    // ADDRESS VALUES
    const shippingLines = order.shippingAddress?.formatted;
    const billingLines  = order.billingAddress?.formatted;

    // RENDER ADDRESSES
    this._renderAddress('[data-shipping-address]', shippingLines);
    this._renderAddress('[data-billing-address]', billingLines);
  }

  // RENDERS ONE ADDRESS BLOCK FROM AN ARRAY OF PRE-FORMATTED LINES
  _renderAddress(selector, lines) {
    // DOM ELEMENTS
    const slot = this.container.querySelector(selector);

    // RESET
    slot.innerHTML = '';

    if (!lines?.length) {
      const placeholder = this._createElements('p', null, null, 'No address was required for this order.');
      slot.appendChild(placeholder);
      return;
    }

    // ADDRESS ELEMENTS
    const [firstLine, ...otherLines] = lines;
    const addressParagraph = this._createElements('p', ['AccountAddress'], null, null);
    const firstLineSpan = this._createElements('span', null, null, firstLine);

    // CREATE DOM ELEMENTS
    addressParagraph.appendChild(firstLineSpan);
    otherLines.forEach((line) => {
      addressParagraph.appendChild(this._createElements('br', null, null, null));
      addressParagraph.appendChild(document.createTextNode(line));
    });

    // APPEND
    slot.appendChild(addressParagraph);
  }

  // ----------------------------------------------
  // BUILDERS
  // ----------------------------------------------

  // BUILDS A SINGLE LINE ITEM'S <tr>: IMAGE + INFO CELL, QUANTITY CELL, LINE TOTAL CELL
  _buildLineItemRow(item) {
    // ROW ELEMENTS
    const row      = this._createElements('tr', null, null, null);
    const mainCell = this._createElements('td', null, null, null);

    // variant.product IS null FOR DELETED PRODUCTS
    const product = item.variant?.product;

    // IMAGE ELEMENTS
    const imageWrapper = this._createElements('div', ['CartItem__ImageWrapper', 'AspectRatio'], null, null);

    // ONLY RENDER THE IMAGE NODE WHEN THE ORDER ACTUALLY HAS ONE — AVOIDS BROKEN-IMAGE ICONS
    if (item.image) {
      const imgSrc      = item.image.url.replace(/(\.(jpg|jpeg|png|webp|gif))(\?|$)/i, '_240x$1$3');
      const imgAlt      = item.image.altText || '';
      const aspectRatio = item.image.width / item.image.height;

      const aspect = this._createElements('div', ['AspectRatio'], [{ style: `--aspect-ratio: ${aspectRatio}` }], null);
      const img    = this._createElements('img', ['CartItem__Image'], [{ src: imgSrc }, { alt: imgAlt }], null);
      aspect.appendChild(img);
      imageWrapper.appendChild(aspect);
    }

    // INFO ELEMENTS
    const info         = this._createElements('div', ['CartItem__Info'], null, null);
    const titleHeading = this._createElements('h2', ['CartItem__Title', 'Heading'], null, null);

    if (product?.handle) {
      titleHeading.appendChild(this._createElements('a', null, [{ href: `/products/${product.handle}` }], item.title));
    } else {
      titleHeading.appendChild(document.createTextNode(item.title));
    }

    // WEIGHT + FIBER BADGES (YARN PRODUCTS ONLY)
    let metaDataEl = null;
    const weight   = product?.meta_weight?.value;
    const fiber    = product?.meta_fiber?.value;

    if (weight && fiber) {
      metaDataEl = this._createElements('p', ['ProductMeta__Descriptions'], null, null);
      metaDataEl.appendChild(this._createElements('span', ['ProductMeta__Descriptions--weight'], null, weight));
      metaDataEl.appendChild(document.createTextNode(` ${fiber}`));
    }

    const metaWrapper = this._createElements('div', ['CartItem__Meta', 'Heading', 'Text--subdued'], null, null);

    if (item.variant?.title && !product?.hasOnlyDefaultVariant) {
      metaWrapper.appendChild(this._createElements('p', ['CartItem__Variant'], null, item.variant.title));
    }

    const validProperties = (item.customAttributes || []).filter((p) => p.value && p.key?.charAt(0) !== '_');

    if (validProperties.length > 0) {
      const propertiesList = this._createElements('ul', ['CartItem__PropertyList'], null, null);
      validProperties.forEach((p) => {
        propertiesList.appendChild(this._createElements('li', ['CartItem__Property'], null, `${p.key}: ${p.value}`));
      });
      metaWrapper.appendChild(propertiesList);
    }

    // REMOVE CART-LEVEL DISCOUNTS FROM ALLOCATIONS
    const lineLevelDiscounts = item.discountAllocations.filter((a) =>
      a.discountApplication.targetSelection !== 'ALL' &&
      Number(a.allocatedAmountSet.presentmentMoney.amount) > 0
    );

    // SUM THE LINE-LEVEL DISCOUNTS TO BE USED FOR FALLBACK PRICING (SHOPIFY MAY NOT CORRECTLY INCLUDE DISCOUNTED PRICES FOR SOME ORDERS)
    const lineLevelDiscountTotal = lineLevelDiscounts.reduce((sum, allocation) =>
      sum + Number(allocation.allocatedAmountSet.presentmentMoney.amount),
    0);

    // PRICE FIELDS (UNIT + LINE)
    const originalUnit = Number(item.originalUnitPriceSet.presentmentMoney.amount);
    const originalLine = Number(item.originalTotalSet.presentmentMoney.amount);
    let finalUnit = Number(item.discountedUnitPriceSet.presentmentMoney.amount);
    let finalLine = Number(item.discountedTotalSet.presentmentMoney.amount);

    // CHECK IF THE LINE-LEVEL DISCOUNTS ARE SIGNIFICANT ENOUGH TO WARRANT FALLBACK PRICING
    const hasLineLevelDiscount = lineLevelDiscountTotal > 0;
    const unitLooksUndiscounted = Math.abs(finalUnit - originalUnit) < 0.000001;
    const lineLooksUndiscounted = Math.abs(finalLine - originalLine) < 0.000001;
    const shouldFallbackPricing = hasLineLevelDiscount && unitLooksUndiscounted && lineLooksUndiscounted;

    if (shouldFallbackPricing) {
      finalLine = Math.max(0, originalLine - lineLevelDiscountTotal);
      finalUnit = item.quantity > 0 ? Math.max(0, finalLine / item.quantity) : finalLine;
    }

    const priceList = this._createElements('div', ['CartItem__PriceList'], null, null);
    this._buildPriceCell(priceList, originalUnit, finalUnit);

    if (lineLevelDiscounts.length > 0) {
      const discountsList = this._createElements('ul', ['CartItem__DiscountList'], null, null);
      lineLevelDiscounts.forEach((allocation) => {
        const app  = allocation.discountApplication;
        const li   = this._createElements('li', ['CartItem__Discount'], null, null);
        const text = this._createElements('span', null, null, `${app.title || app.code}: -${this._formatMoney(allocation.allocatedAmountSet.presentmentMoney.amount)}`);

        li.appendChild(text);
        discountsList.appendChild(li);
      });
      priceList.appendChild(discountsList);
    }

    metaWrapper.appendChild(priceList);

    // CREATE DOM ELEMENTS
    info.appendChild(titleHeading);
    if (metaDataEl) info.appendChild(metaDataEl);
    info.appendChild(metaWrapper);

    mainCell.appendChild(imageWrapper);
    mainCell.appendChild(info);

    // SUMMARY CELLS
    const qtyCell       = this._createElements('td', ['Text--alignCenter', 'Heading', 'Text--subdued', 'hidden-phone'], null, String(item.quantity));
    const lineTotalCell = this._createElements('td', ['Text--alignRight', 'Heading', 'Text--subdued'], null, null);
    this._buildPriceCell(lineTotalCell, originalLine, finalLine);

    // APPEND
    row.appendChild(mainCell);
    row.appendChild(qtyCell);
    row.appendChild(lineTotalCell);
    return row;
  }

  // APPENDS EITHER A SINGLE Price SPAN OR A HIGHLIGHT + STRIKETHROUGH PAIR TO THE GIVEN CELL
  _buildPriceCell(cell, original, final) {
    if (original !== final) {
      // PRICE ELEMENTS
      const finalPrice    = this._createElements('span', ['CartItem__Price', 'Price', 'Price--highlight'], null, this._formatMoney(final));
      const space         = document.createTextNode(' ');
      const originalPrice = this._createElements('span', ['CartItem__OriginalPrice', 'Price', 'Price--compareAt'], null, this._formatMoney(original));

      // APPEND
      cell.appendChild(finalPrice);
      cell.appendChild(space);
      cell.appendChild(originalPrice);
    } else {
      // PRICE ELEMENTS
      const finalPrice = this._createElements('span', ['CartItem__Price', 'Price'], null, this._formatMoney(final));

      // APPEND
      cell.appendChild(finalPrice);
    }
  }

  // BUILDS THE "YOUR PRODUCT X HAS BEEN SENT." BANNER ROW FOR A FULFILLED ITEM WITHOUT TRACKING
  _buildSentBanner(item) {
    // ROW ELEMENTS
    const row    = this._createElements('tr', null, null, null);
    const cell   = this._createElements('td', null, [{ colspan: '3' }], null);
    const banner = this._createElements('p', ['Alert', 'Alert--large', 'Alert--success'], null, `Your product "${item.title}" has been sent.`);

    // APPEND
    cell.appendChild(banner);
    row.appendChild(cell);
    return row;
  }

  // BUILDS THE "YOUR ORDER HAS BEEN SENT" BANNER WITH A SINGLE TRACKING LINK
  _buildTrackingAlert(tracking) {
    // ALERT ELEMENTS
    const alert        = this._createElements('p', ['Alert', 'Alert--large', 'Alert--success'], [{ role: 'status' }], null);
    const alertText    = document.createTextNode(`Your order has been sent. Track the shipment with number ${tracking.number} or by clicking here: `);
    const trackingLink = this._buildTrackingLink(tracking);

    // APPEND
    alert.appendChild(alertText);
    alert.appendChild(trackingLink);
    return alert;
  }

  // BUILDS A SINGLE TRACKING LINK; FALLS BACK TO THE TRACKING NUMBER AS LINK TEXT WHEN NO URL
  _buildTrackingLink(tracking) {
    if (tracking.url) {
      const linkElement = this._createElements('a', ['Link', 'Link--underlineNative'], [{ href: tracking.url }, { target: '_blank' }, { rel: 'noopener noreferrer' }], tracking.url);
      return linkElement;
    }

    const textElement = this._createElements('span', null, null, tracking.number);
    return textElement;
  }

  // BUILDS A SUMMARY ROW: "LABEL (TITLE)" ON THE LEFT, AMOUNT (OR -AMOUNT) ON THE RIGHT
  _buildFooterRow(label, title, amount, isNegative = false) {
    // ROW ELEMENTS
    const row = this._createElements('tr', null, null, null);
    const spacerCell = this._createElements('td', ['hidden-phone'], null, null);
    const labelCell = this._createElements('td', null, null, null);
    const labelSpan = this._createElements('span', ['Heading', 'Text--subdued', 'u-h7'], null, label);

    // APPEND LABEL
    labelCell.appendChild(labelSpan);

    if (title) {
      // TITLE ELEMENTS
      const titleSpace = document.createTextNode(' ');
      const titleSpan = this._createElements('span', ['Text--subdued'], null, `(${title})`);

      // APPEND TITLE
      labelCell.appendChild(titleSpace);
      labelCell.appendChild(titleSpan);
    }

    const amountCell = this._createElements('td', ['Heading', 'Text--subdued', 'Text--alignRight', 'u-h7'], null, `${isNegative ? '-' : ''}${this._formatMoney(amount)}`);

    // APPEND ROW
    row.appendChild(spacerCell);
    row.appendChild(labelCell);
    row.appendChild(amountCell);
    return row;
  }

  // BUILDS THE FINAL "TOTAL" ROW (HEAVIER u-h6 TYPOGRAPHY THAN THE OTHER SUMMARY ROWS)
  _buildTotalRow(amount) {
    // ROW ELEMENTS
    const row        = this._createElements('tr', null, null, null);
    const spacerCell = this._createElements('td', ['hidden-phone'], null, null);
    const labelCell  = this._createElements('td', ['Heading', 'u-h6'], null, 'Total');
    const amountCell = this._createElements('td', ['Heading', 'Text--alignRight', 'u-h6'], null, this._formatMoney(amount));

    // APPEND
    row.appendChild(spacerCell);
    row.appendChild(labelCell);
    row.appendChild(amountCell);
    return row;
  }

  // ----------------------------------------------
  // UTILITIES
  // ----------------------------------------------

  // DELEGATES TO THE THEME'S SHARED Currency.formatMoney HELPER (EXPOSED ON window.theme FROM theme.js).
  // MATCHES LIQUID'S DISPLAY CONVENTION OF AMOUNT + ISO CODE).
  _formatMoney(amount) {
    // GRAPHQL RETURNS DOLLARS, CURRENCY.FORMATMONEY EXPECTS CENTS
    const cents = Math.round(Number(amount) * 100);

    const format = `{{amount}} ${this.orderCurrency}`;

    return window.theme.formatMoney(cents, format);
  }

  // FORMATS AN ISO TIMESTAMP AS "April 02, 2026" OR "April 02, 2026 at 01:54PM" (NEW YORK TIME)
  _formatDate(iso, withTime = false) {
    if (!iso) return null;

    const date     = new Date(iso);
    const datePart = date.toLocaleString('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'long', day: '2-digit' });

    if (!withTime) return datePart;

    const timePart = date.toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: true }).replace(' ', '');
    return `${datePart} at ${timePart}`;
  }

  // SHOWS A USER-SAFE ERROR BANNER AND LOGS THE REAL ERROR DETAILS FOR DEBUGGING/MONITORING
  _setErrorMessage(error = null, context = 'unknown', message = 'An unexpected error occurred. Please return to the login page and try again.') {
    // DOM ELEMENTS
    const alerts = this.container.querySelector('[data-order-alerts]');

    // CLEAR ALL SKELETON PLACEHOLDERS ON ERROR
    this._clearSkeletons();

    // RESET
    alerts.innerHTML = '';

    // LOG ACTUAL ERROR DETAILS
    if (error) {
      console.error('[OrderStatusSection] Failed to render order status.', {
        context,
        error,
      });
    }

    // ALERT ELEMENTS
    const errorAlert = this._createElements('p', ['Alert', 'Alert--large', 'Alert--error'], [{ role: 'alert' }], message);

    // APPEND
    alerts.appendChild(errorAlert);
  }

  // REMOVES ALL ORDER STATUS SKELETON PLACEHOLDERS
  _clearSkeletons() {
    this.container
      .querySelectorAll('.skeleton, [data-order-line-item-skeleton], [data-order-summary-skeleton]')
      .forEach((element) => element.remove());
  }

  // CREATES AN ELEMENT WITH OPTIONAL CLASSES, ATTRIBUTES (E.G. [{ href: '/x' }]), AND TEXT
  // ELEMENT: ELEMENT YOU WANT TO CREATE
  // CLASSNAME: CLASS'S YOU WANT TO ADD TO THE ELEMENT (OPTIONAL)
  // ATTRIBUTESARRAY: ARRAY OF ATTRIBUTES TO ADD TO THE ELEMENT (OPTIONAL)
  // TEXT: TEXT TO SET AS THE ELEMENT'S CONTENT (OPTIONAL)
  _createElements(element, className, attributesArray, text) {
    var domElement = document.createElement(element);
    className ? domElement.classList.add(...className) : null;
    if(attributesArray && attributesArray.length >= 1) {
      for(var i = 0; i < attributesArray.length; i++) {
        var domElementAttributeObject = attributesArray[i];
        for(const key in domElementAttributeObject) {
          domElement.setAttribute(`${key}`, `${domElementAttributeObject[key]}`);
        }
      }
    }

    text ? domElement.innerText = text : null;

    return domElement;
  }
}

customElements.define('order-status-section', OrderStatusSection);
